import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  EmptyState,
  FilterChips,
  RightSidePanel,
  SearchInput,
  Tabs,
  Toggle,
  useToast,
} from '@teals/ui-web';
import { useAuth } from '../../hooks/useAuth';
import { useOrgRole } from '../../hooks/useOrgRole';
import { useGetOrgModelsQuery } from '../../store/api/orgModelsApi';
import { useSendMessageMutation } from '../../store/api/chatApi';
import type { OrgModel } from '../../store/api/orgModelsApi';
import { modelMetaLine } from '../../utils/modelDisplay';
import {
  MAX_COMPARE,
  canManageModels,
  canTryModels,
  costTierDots,
  costTierLabel,
  formatTokens,
  topScore,
} from './modelsShared';
import {
  ActionBar,
  CostTierText,
  Header,
  HeaderCopy,
  Meta,
  Muted,
  Page,
  Select,
  Subtitle,
  Title,
} from './modelsStyles';
import { useModelToggle } from './useModelToggle';

type SortKey = 'best_match' | 'quality' | 'cost' | 'speed' | 'newest';
type SortDir = 'asc' | 'desc';
type TaskTab = 'chat' | 'image' | 'video' | 'more';

const DEFAULT_SORT_DIR: Record<SortKey, SortDir> = {
  best_match: 'asc',
  quality: 'desc',
  cost: 'asc',
  speed: 'asc',
  newest: 'desc',
};

const TASK_OF: Record<string, TaskTab> = {
  chat: 'chat',
  image_generation: 'image',
  video_generation: 'video',
  embedding: 'more',
  video_understanding: 'more',
};

function modelTask(model: OrgModel): TaskTab {
  if (model.task && TASK_OF[model.task]) return TASK_OF[model.task];
  if (model.chatCapable === false) {
    if (model.family === 'embed' || model.capabilities?.includes('embeddings')) return 'more';
    return 'more';
  }
  return 'chat';
}

function representative(ways: OrgModel[]): OrgModel {
  return ways.find((way) => way.enabled) || ways[0];
}

const SortControl = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
`;

const DirButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: ${(p) => (p.$active ? 'var(--accent-subtle)' : 'var(--bg-surface)')};
  color: ${(p) => (p.$active ? 'var(--accent)' : 'var(--text-muted)')};
  font-size: 0.875rem;
  line-height: 1;
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
  }
`;

const Filters = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;

  > :first-child {
    flex: 1 1 240px;
    max-width: 360px;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1.5rem minmax(0, 2.4fr) 6rem 5rem 7.5rem 5.5rem 5rem;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 1rem;
  background: var(--bg-surface);
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1.5rem minmax(0, 1fr) auto;

    > [data-col='wide'] {
      display: none;
    }
  }
`;

const HeadRow = styled(Row)`
  padding-top: 0.625rem;
  padding-bottom: 0.625rem;
  background: var(--bg-elevated);
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
`;

const NameLink = styled(Link)`
  font-weight: 600;
  color: var(--text-primary);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const BestFor = styled.span`
  display: block;
  margin-top: 0.125rem;
  font-size: 0.8125rem;
  color: var(--text-secondary);
`;

const Score = styled.span`
  font-size: 0.8125rem;
  color: var(--text-primary);
`;

const Tray = styled.div`
  position: sticky;
  bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding: 0.75rem 1rem;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--bg-surface);
  box-shadow: var(--shadow-lg);
`;

const TrayChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  background: var(--accent-subtle);
  color: var(--accent);
  font-size: 0.8125rem;

  button {
    border: none;
    background: none;
    color: inherit;
    cursor: pointer;
    font: inherit;
    padding: 0;
  }
`;

function sortFamilies(families: OrgModel[][], sort: SortKey, dir: SortDir): OrgModel[][] {
  if (sort === 'best_match') return dir === 'desc' ? [...families].reverse() : families;
  const copy = [...families];
  const sign = dir === 'asc' ? 1 : -1;
  const score = (ways: OrgModel[]) => topScore(representative(ways))?.score ?? -1;
  const cost = (ways: OrgModel[]) => representative(ways).costTier ?? 99;
  const speed = (ways: OrgModel[]) => ways[0].speed?.p50TtftMs ?? 1_000_000_000;
  const added = (ways: OrgModel[]) => ways[0].addedOn || '';
  const cmp = (a: OrgModel[], b: OrgModel[]) => {
    if (sort === 'quality') return score(a) - score(b);
    if (sort === 'cost') return cost(a) - cost(b);
    if (sort === 'speed') return speed(a) - speed(b);
    return added(a).localeCompare(added(b));
  };
  copy.sort((a, b) => sign * cmp(a, b));
  return copy;
}

function matchesQuery(model: OrgModel, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = [
    model.displayName,
    model.productId,
    model.lab,
    model.wayLabel,
    ...(model.bestFor || []),
    ...(model.tags || []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return q.split(/\s+/).every((word) => hay.includes(word));
}

const Hint = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: var(--text-secondary);

  button {
    border: none;
    background: none;
    color: var(--accent);
    font: inherit;
    cursor: pointer;
    padding: 0;
    text-decoration: underline;
  }
`;

const Popover = styled.div`
  position: relative;
`;

const PopoverPanel = styled.div`
  position: absolute;
  z-index: 5;
  top: calc(100% + 0.375rem);
  left: 0;
  width: min(28rem, 80vw);
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--bg-surface);
  box-shadow: var(--shadow-lg);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FacetLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  margin-bottom: 0.25rem;
`;

const TASK_LABEL: Record<TaskTab, string> = {
  chat: 'chat',
  image: 'image',
  video: 'video',
  more: 'other',
};

const Models: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const { role } = useOrgRole();
  const canManage = canManageModels(role);
  const canTry = canTryModels(role);
  const { show } = useToast();
  const { data: models = [], isLoading, isError } = useGetOrgModelsQuery(orgId || '', {
    skip: !orgId,
  });
  const { toggle, patching, disableModal } = useModelToggle(orgId);

  const task = (searchParams.get('task') as TaskTab) || 'chat';
  const scope = searchParams.get('scope') === 'all' ? 'all' : 'enabled';
  const query = searchParams.get('q') || '';
  const sortParam = searchParams.get('sort') || 'best_match';
  const sort: SortKey = sortParam in DEFAULT_SORT_DIR ? (sortParam as SortKey) : 'best_match';
  const requestedDir = searchParams.get('dir');
  const dir: SortDir =
    requestedDir === 'asc' || requestedDir === 'desc' ? requestedDir : DEFAULT_SORT_DIR[sort];
  const tagParam = searchParams.get('tags') || '';
  const activeTags = tagParam ? tagParam.split(',').filter(Boolean) : [];
  const [selected, setSelected] = useState<string[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [advisorDraft, setAdvisorDraft] = useState('');
  const [advisorReply, setAdvisorReply] = useState('');
  const [sendAdvisor] = useSendMessageMutation();

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value) next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  const inTask = useMemo(
    () => models.filter((model) => modelTask(model) === task && model.lifecycle?.stage !== 'retired'),
    [models, task],
  );
  const onCount = useMemo(() => {
    const keys = new Set<string>();
    for (const model of inTask) {
      if (model.enabled) keys.add(model.modelKey || model.productId);
    }
    return keys.size;
  }, [inTask]);
  const allCount = useMemo(() => {
    return new Set(inTask.map((model) => model.modelKey || model.productId)).size;
  }, [inTask]);

  const families = useMemo(() => {
    const grouped = new Map<string, OrgModel[]>();
    for (const model of inTask) {
      const key = model.modelKey || model.productId;
      const ways = grouped.get(key) || [];
      ways.push(model);
      grouped.set(key, ways);
    }
    let rows = [...grouped.values()];
    if (scope === 'enabled') {
      rows = rows.filter((ways) => ways.some((way) => way.enabled));
    }
    if (query.trim()) {
      rows = rows.filter((ways) => ways.some((way) => matchesQuery(way, query)));
    }
    if (activeTags.length) {
      rows = rows.filter((ways) =>
        ways.some((way) => activeTags.every((tag) => (way.tags || []).includes(tag) || (way.capabilities || []).includes(tag))),
      );
    }
    return sortFamilies(rows, sort, dir);
  }, [inTask, scope, query, activeTags, sort, dir]);

  const offMatches = useMemo(() => {
    if (scope !== 'enabled' || !query.trim()) return 0;
    return inTask.filter((model) => !model.enabled && matchesQuery(model, query)).length;
  }, [inTask, scope, query]);

  const tabs = useMemo(() => {
    const present = new Set(models.map(modelTask));
    const items: { id: TaskTab; label: string }[] = [{ id: 'chat', label: 'Chat' }];
    if (present.has('image')) items.push({ id: 'image', label: 'Image' });
    if (present.has('video')) items.push({ id: 'video', label: 'Video' });
    if (present.has('more')) items.push({ id: 'more', label: 'More' });
    return items;
  }, [models]);

  const toggleSelected = (productId: string) => {
    setSelected((current) => {
      if (current.includes(productId)) return current.filter((id) => id !== productId);
      if (current.length >= MAX_COMPARE) {
        show({ message: `Compare up to ${MAX_COMPARE} models at a time.`, type: 'info' });
        return current;
      }
      return [...current, productId];
    });
  };

  const nameFor = (productId: string) =>
    models.find((model) => model.productId === productId)?.displayName || productId;

  const renderRow = (ways: OrgModel[]) => {
    const model = representative(ways);
    const score = topScore(model);
    const isSelected = selected.includes(model.productId);
    const wayCount = ways.length;
    return (
      <Row key={model.modelKey || model.productId} data-testid={`model-row-${model.productId}`}>
        <input
          type="checkbox"
          aria-label={`Compare ${model.displayName}`}
          checked={isSelected}
          disabled={model.chatCapable === false && task === 'more'}
          onChange={() => toggleSelected(model.productId)}
        />
        <div style={{ minWidth: 0 }}>
          <NameLink to={`/models/${encodeURIComponent(model.productId)}`}>
            {model.displayName}
          </NameLink>
          <Meta>
            {modelMetaLine(model)}
            {wayCount > 1 ? ` · ${wayCount} ways to run` : ''}
          </Meta>
          {query.trim() && model.tags?.length ? (
            <BestFor>Matches: {model.tags.slice(0, 3).join(' · ')}</BestFor>
          ) : (
            model.bestFor?.[0] && <BestFor>{model.bestFor[0]}</BestFor>
          )}
        </div>
        <CostTierText data-col="wide" title="Relative cost">
          {costTierLabel(model.costTier)}
          <span aria-hidden>{costTierDots(model.costTier)}</span>
        </CostTierText>
        <Score data-col="wide">{formatTokens(model.contextWindow)}</Score>
        <Score data-col="wide">
          {score ? (
            <>
              {score.score}% <Meta>{score.label}</Meta>
            </>
          ) : (
            <Meta>Not yet benchmarked</Meta>
          )}
        </Score>
        <div data-col="wide">
          {canManage && wayCount === 1 ? (
            <Toggle
              value={model.enabled}
              onChange={() => void toggle(model)}
              disabled={patching}
              label={model.enabled ? 'On' : 'Off'}
            />
          ) : model.enabled ? (
            <Badge variant="success" size="sm">On</Badge>
          ) : canManage ? (
            <Button variant="outline" size="sm" onClick={() => navigate(`/models/${encodeURIComponent(model.productId)}`)}>
              Turn on…
            </Button>
          ) : (
            <Badge variant="neutral" size="sm">Off</Badge>
          )}
        </div>
        <div>
          {task !== 'more' && canTry && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/models/${encodeURIComponent(model.productId)}/playground`)}
            >
              Try
            </Button>
          )}
        </div>
      </Row>
    );
  };

  if (!orgId) return null;
  const hidden = allCount - onCount;
  const taskWord = TASK_LABEL[task] || 'chat';

  return (
    <Page>
      <Header>
        <HeaderCopy>
          <Title>Models</Title>
          <Subtitle>Compare, try, and choose the models your organisation uses.</Subtitle>
        </HeaderCopy>
        <ActionBar>
          <Button variant="outline" onClick={() => navigate('/models?advisor=open')}>
            Ask about models
          </Button>
          <Button variant="outline" onClick={() => navigate('/models/connect')}>
            Use in your tools
          </Button>
        </ActionBar>
      </Header>

      <Tabs
        items={tabs}
        activeTab={tabs.some((item) => item.id === task) ? task : 'chat'}
        onChange={(id) => {
          setSelected([]);
          setParam('task', id === 'chat' ? '' : id);
        }}
      />

      <Filters>
        <SearchInput
          value={query}
          onChange={(value) => setParam('q', value)}
          placeholder="Search by name, lab, or what you need…"
          debounceMs={0}
        />
        <FilterChips
          items={[
            { id: 'enabled', label: 'On for your org', count: onCount },
            { id: 'all', label: 'Entire catalog', count: allCount },
          ]}
          activeId={scope}
          onChange={(id) => setParam('scope', id === 'enabled' ? '' : 'all')}
        />
        <Popover>
          <Button variant="outline" size="sm" onClick={() => setFiltersOpen((open) => !open)}>
            Filters{activeTags.length ? ` · ${activeTags.length}` : ''}
          </Button>
          {filtersOpen && (
            <PopoverPanel>
              <div>
                <FacetLabel>Capabilities</FacetLabel>
                <FilterChips
                  items={[
                    { id: 'vision', label: 'Vision' },
                    { id: 'tools', label: 'Tools' },
                    { id: 'reasoning', label: 'Reasoning' },
                  ]}
                  activeId={activeTags[0] || ''}
                  onChange={(id) => setParam('tags', activeTags.includes(id) ? '' : id)}
                />
              </div>
            </PopoverPanel>
          )}
        </Popover>
        <SortControl>
          <Select
            aria-label="Sort"
            value={sort}
            onChange={(event) => setParam('sort', event.target.value === 'best_match' ? '' : event.target.value)}
          >
            <option value="best_match">Sort: Best match</option>
            <option value="quality">Sort: Quality</option>
            <option value="cost">Sort: Cost</option>
            <option value="speed">Sort: Speed</option>
            <option value="newest">Sort: Newest</option>
          </Select>
          <DirButton
            type="button"
            $active={dir === 'asc'}
            aria-label="Sort ascending"
            aria-pressed={dir === 'asc'}
            onClick={() => setParam('dir', DEFAULT_SORT_DIR[sort] === 'asc' ? '' : 'asc')}
          >
            ↑
          </DirButton>
          <DirButton
            type="button"
            $active={dir === 'desc'}
            aria-label="Sort descending"
            aria-pressed={dir === 'desc'}
            onClick={() => setParam('dir', DEFAULT_SORT_DIR[sort] === 'desc' ? '' : 'desc')}
          >
            ↓
          </DirButton>
        </SortControl>
      </Filters>

      {scope === 'enabled' && !query.trim() && hidden > 0 && (
        <Hint>
          Showing the {onCount} {taskWord} models that are on. {hidden} more are in the catalog.{' '}
          <button type="button" onClick={() => setParam('scope', 'all')}>
            Browse the entire catalog
          </button>
        </Hint>
      )}
      {scope === 'enabled' && query.trim() && offMatches > 0 && (
        <Hint>
          {offMatches} more model{offMatches === 1 ? '' : 's'} match among models that are off for your organisation.{' '}
          <button type="button" onClick={() => setParam('scope', 'all')}>
            Browse the entire catalog
          </button>
        </Hint>
      )}
      {query.trim().split(/\s+/).length >= 3 && (
        <Hint>
          Describing a use case?{' '}
          <button type="button" onClick={() => navigate(`/models?advisor=open&q=${encodeURIComponent(query)}`)}>
            Ask Model Advisor
          </button>
        </Hint>
      )}

      {isLoading ? (
        <Muted>Loading models…</Muted>
      ) : isError ? (
        <EmptyState title="Couldn't load models" description="Check your connection and try again." />
      ) : families.length === 0 ? (
        <EmptyState
          title={onCount === 0 && scope === 'enabled' ? `No ${taskWord} models are on yet` : 'No models match'}
          description={
            scope === 'enabled'
              ? 'Browse the entire catalog to turn one on.'
              : 'Try a different search or filter.'
          }
          action={
            scope === 'enabled' ? (
              <Button variant="outline" onClick={() => setParam('scope', 'all')}>
                Browse all models
              </Button>
            ) : undefined
          }
        />
      ) : (
        <List role="table" aria-label="Models">
          <HeadRow role="row">
            <span />
            <span>Model</span>
            <span data-col="wide">Cost</span>
            <span data-col="wide">Context</span>
            <span data-col="wide">Top score</span>
            <span data-col="wide">Status</span>
            <span />
          </HeadRow>
          {families.map(renderRow)}
        </List>
      )}

      <Muted>
        Cost is relative to other models on Luminafactory, not a rate. Benchmarks are reported by each lab.
        {!canManage && ' Only Owners and Admins can turn models on or off.'}
      </Muted>

      {selected.length > 0 && (
        <Tray role="region" aria-label="Compare tray">
          {selected.map((id) => (
            <TrayChip key={id}>
              {nameFor(id)}
              <button type="button" aria-label={`Remove ${nameFor(id)}`} onClick={() => toggleSelected(id)}>
                ×
              </button>
            </TrayChip>
          ))}
          <Meta>
            {selected.length} of {MAX_COMPARE}
          </Meta>
          <ActionBar style={{ marginLeft: 'auto' }}>
            <Button variant="ghost" size="sm" onClick={() => setSelected([])}>
              Clear
            </Button>
            {canTry && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/models/playground?ids=${selected.join(',')}`)}
              >
                Try together
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              disabled={selected.length < 2}
              onClick={() => navigate(`/models/compare?ids=${selected.join(',')}`)}
            >
              Compare
            </Button>
          </ActionBar>
        </Tray>
      )}

      <RightSidePanel
        open={searchParams.get('advisor') === 'open'}
        title="Model Advisor"
        onClose={() => setParam('advisor', '')}
      >
        <Subtitle>
          Recommendations come from the catalog. A test runs only after you press Run.
        </Subtitle>
        <textarea
          aria-label="Ask Model Advisor"
          value={advisorDraft || searchParams.get('q') || ''}
          onChange={(event) => setAdvisorDraft(event.target.value)}
          placeholder="What do you need the model to do?"
        />
        <Button
          variant="primary"
          onClick={() => {
            const text = (advisorDraft || searchParams.get('q') || '').trim();
            if (!text || !orgId) return;
            void sendAdvisor({
              query: text,
              agentName: 'model-advisor',
              metadata: { source: 'model_advisor_chat', prompt_context: { page: 'models' } },
            })
              .unwrap()
              .then((result) => setAdvisorReply(result.response || 'Working on a recommendation…'))
              .catch(() => setAdvisorReply('The advisor could not reply. Try again.'));
          }}
        >
          Ask
        </Button>
        {advisorReply && <Muted>{advisorReply}</Muted>}
      </RightSidePanel>

      {disableModal}
    </Page>
  );
};

export default Models;
